/**
 * What: The filtered-empty state shown inside a resource index when active filters match no rows.
 * Transport & data-flow: Pure presentational; the caller owns copy, loading, and filter state.
 *
 * No Card/Box wrapper on purpose: Polaris IndexTable renders its emptyState slot inside its own
 * container, so any card chrome here would double-card (#3260). Copy always arrives via props so
 * every merchant string stays behind a product-language key at the call site.
 */
import { BlockStack, Button, Text } from '@shopify/polaris'

type FilteredEmptyProps = {
  title: string
  body: string
  clearFiltersLabel: string
  onClearFilters?: () => void
  /** Replaces the whole state with one subdued line while the index is refreshing. */
  isLoading?: boolean
  /** Subdued line rendered while `isLoading` is true. */
  refreshingBody?: string
}

/** One shared filtered-empty message with its clear-filters action, for all resource indexes. */
export function FilteredEmpty({
  title,
  body,
  clearFiltersLabel,
  onClearFilters,
  isLoading,
  refreshingBody,
}: FilteredEmptyProps) {
  if (isLoading) {
    return (
      <Text as="p" tone="subdued">
        {refreshingBody}
      </Text>
    )
  }
  return (
    <BlockStack gap="300">
      <Text as="h3" variant="headingMd">
        {title}
      </Text>
      <Text as="p" tone="subdued">
        {body}
      </Text>
      {onClearFilters && <Button onClick={onClearFilters}>{clearFiltersLabel}</Button>}
    </BlockStack>
  )
}
